import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { 
  Bot, 
  FileSearch, 
  Brain, 
  FileStack, 
  Sparkles, 
  Shield, 
  Sun,
  Moon,
  MessageSquare,
  ArrowRight,
  CheckCircle,
  FileText,
  Table,
  PresentationIcon,
  Files,
  TrendingUp,
  Users,
  LineChart,
  Lock,
  Share2,
  Building2,
  ChevronDown,
  Send
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useGoogleLogin } from '@react-oauth/google';
import axios from 'axios';
import { cn } from '../lib/utils';
import Footer from './Footer';

// Add translations object
const translations = {
  en: {
    meta: {
      title: "Predica.AI - AI-Powered Document Intelligence Platform | Transform Your Document Experience",
      description: "Transform your document workflow with Predica.AI's AI-powered analysis. Upload documents, ask questions naturally, and get instant insights with advanced AI technology.",
    },
    nav: {
      features: "Features",
      pricing: "Pricing",
      getStarted: "Get Started"
    },
    hero: {
      badge: "AI-Powered Document Analysis",
      title: {
        line1: "Your Personal",
        line2: "Document Assistant"
      },
      description: "Upload any document and chat with it naturally. Get instant answers, summaries, and insights - like having a smart study buddy."
    }
  },
  de: {
    meta: {
      title: "Predica.AI - KI-gestützte Dokumentenintelligenz-Plattform | Transformieren Sie Ihre Dokumentenerfahrung",
      description: "Transformieren Sie Ihren Dokumenten-Workflow mit Predica.AIs KI-gestützter Analyse. Laden Sie Dokumente hoch, stellen Sie Fragen natürlich und erhalten Sie sofortige Einblicke.",
    },
    nav: {
      features: "Funktionen",
      pricing: "Preise",
      getStarted: "Jetzt Starten"
    },
    hero: {
      badge: "KI-gestützte Dokumentenanalyse",
      title: {
        line1: "Ihr Persönlicher",
        line2: "Dokumenten-Assistent"
      },
      description: "Laden Sie ein Dokument hoch und chatten Sie damit natürlich. Erhalten Sie sofortige Antworten, Zusammenfassungen und Erkenntnisse."
    }
  },
  fr: {
    meta: {
      title: "Predica.AI - Plateforme d'Intelligence Documentaire Alimentée par l'IA",
      description: "Transformez votre flux de travail documentaire avec l'analyse alimentée par l'IA de Predica.AI. Téléchargez des documents, posez des questions naturellement.",
    },
    nav: {
      features: "Fonctionnalités",
      pricing: "Tarifs",
      getStarted: "Commencer"
    },
    hero: {
      badge: "Analyse de Documents par IA",
      title: {
        line1: "Votre Assistant",
        line2: "Personnel de Documents"
      },
      description: "Téléchargez n'importe quel document et discutez naturellement. Obtenez des réponses instantanées, des résumés et des aperçus."
    }
  },
  es: {
    meta: {
      title: "Predica.AI - Plataforma de Inteligencia Documental Impulsada por IA",
      description: "Transforme su flujo de trabajo documental con el análisis impulsado por IA de Predica.AI. Suba documentos, haga preguntas naturalmente.",
    },
    nav: {
      features: "Características",
      pricing: "Precios",
      getStarted: "Comenzar"
    },
    hero: {
      badge: "Análisis de Documentos con IA",
      title: {
        line1: "Tu Asistente",
        line2: "Personal de Documentos"
      },
      description: "Sube cualquier documento y chatea con él naturalmente. Obtén respuestas instantáneas, resúmenes y perspectivas."
    }
  },
  it: {
    meta: {
      title: "Predica.AI - Piattaforma di Intelligenza Documentale Basata su IA",
      description: "Trasforma il tuo flusso di lavoro documentale con l'analisi basata su IA di Predica.AI. Carica documenti, fai domande naturalmente.",
    },
    nav: {
      features: "Funzionalità",
      pricing: "Prezzi",
      getStarted: "Inizia Ora"
    },
    hero: {
      badge: "Analisi Documenti con IA",
      title: {
        line1: "Il Tuo Assistente",
        line2: "Personale per Documenti"
      },
      description: "Carica qualsiasi documento e chatta con esso naturalmente. Ottieni risposte istantanee, riassunti e approfondimenti."
    }
  }
};

// Add language selector component
const LanguageSelector = ({ currentLang, onLanguageChange }) => {
  return (
    <select 
      value={currentLang} 
      onChange={(e) => onLanguageChange(e.target.value)}
      className="ml-4 bg-transparent border border-gray-300 dark:border-gray-700 rounded-lg px-2 py-1 text-sm text-gray-600 dark:text-gray-300"
    >
      <option value="en">English</option>
      <option value="de">Deutsch</option>
      <option value="fr">Français</option>
      <option value="es">Español</option>
      <option value="it">Italiano</option>
    </select>
  );
};

const FeaturesSection = () => {
  const features = [
    {
      icon: <Bot className="w-8 h-8" />,
      title: "Chat with Documents",
      description: "Have natural conversations with your documents. Ask questions and get instant answers from any part of your files."
    },
    {
      icon: <FileSearch className="w-8 h-8" />,
      title: "Smart Search",
      description: "Find exactly what you're looking for, even if you don't remember the exact words. Our AI understands what you mean."
    },
    {
      icon: <Brain className="w-8 h-8" />,
      title: "Quick Understanding",
      description: "Get the main points of any document instantly. Perfect for research papers, reports, or long articles."
    },
    {
      icon: <FileStack className="w-8 h-8" />,
      title: "Work with Multiple Files",
      description: "Upload multiple documents and find connections between them. Great for research, study, or project work."
    },
    {
      icon: <Sparkles className="w-8 h-8" />,
      title: "Auto-Summaries",
      description: "Get quick summaries of any document. Save hours of reading time while catching all important details."
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Private & Secure",
      description: "Your documents stay private and secure. We use strong encryption to protect your sensitive information."
    }
  ];

  return (
    <section id="features" className="py-24 bg-white dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-indigo-600 dark:from-purple-400 dark:to-indigo-400 text-transparent bg-clip-text inline-block mb-4">
            Powerful Document Intelligence
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300">
            Transform how you work with documents using our advanced AI features
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <div 
              key={index}
              className="relative group p-8 bg-gray-50 dark:bg-gray-800 rounded-xl shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-200 dark:border-gray-700"
            >
              {/* Icon */}
              <div className="w-12 h-12 rounded-lg bg-purple-100 dark:bg-purple-900/50 flex items-center justify-center text-purple-600 dark:text-purple-400 mb-6 transition-transform group-hover:scale-110">
                {feature.icon}
              </div>

              {/* Title */}
              <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
                {feature.title}
              </h3>

              {/* Description */}
              <p className="text-gray-600 dark:text-gray-300">
                {feature.description}
              </p>

              {/* Hover Effect Line */}
              <div className="absolute bottom-0 left-0 h-1 bg-gradient-to-r from-purple-600 to-indigo-600 transition-all duration-300 w-0 group-hover:w-full" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Update the background styles to be more subtle
const backgroundStyles = `
  @keyframes subtleFloat {
    0%, 100% { transform: translate(0, 0); }
    50% { transform: translate(0, 10px); }
  }

  @keyframes subtlePulse {
    0%, 100% { opacity: 0.3; }
    50% { opacity: 0.1; }
  }

  .subtle-mesh {
    position: fixed;
    inset: 0;
    background-image: 
      radial-gradient(at 40% 20%, rgba(200, 200, 200, 0.03) 0px, transparent 50%),
      radial-gradient(at 80% 0%, rgba(200, 200, 200, 0.03) 0px, transparent 50%),
      radial-gradient(at 0% 50%, rgba(200, 200, 200, 0.03) 0px, transparent 50%),
      radial-gradient(at 80% 50%, rgba(200, 200, 200, 0.03) 0px, transparent 50%);
  }

  .dark .subtle-mesh {
    background-image: 
      radial-gradient(at 40% 20%, rgba(50, 50, 50, 0.03) 0px, transparent 50%),
      radial-gradient(at 80% 0%, rgba(50, 50, 50, 0.03) 0px, transparent 50%),
      radial-gradient(at 0% 50%, rgba(50, 50, 50, 0.03) 0px, transparent 50%),
      radial-gradient(at 80% 50%, rgba(50, 50, 50, 0.03) 0px, transparent 50%);
  }

  .floating-element {
    animation: subtleFloat 8s ease-in-out infinite;
  }

  .subtle-pulse {
    animation: subtlePulse 4s ease-in-out infinite;
  }
`;

// Add this CSS for the animated background
const animatedBackgroundStyles = `
  @keyframes gradient {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }

  .animated-bg {
    background: linear-gradient(-45deg, #6366F1, #8B5CF6, #4F46E5, #7C3AED);
    background-size: 400% 400%;
    animation: gradient 15s ease infinite;
    opacity: 0.05;
  }

  .mesh-grid {
    background-image: radial-gradient(circle at 1px 1px, rgba(255, 255, 255, 0.15) 1px, transparent 0);
    background-size: 40px 40px;
    mask-image: radial-gradient(ellipse at center, black 40%, transparent 70%);
  }

  .floating {
    animation: floating 3s ease-in-out infinite;
  }

  @keyframes floating {
    0% { transform: translateY(0px); }
    50% { transform: translateY(-10px); }
    100% { transform: translateY(0px); }
  }
`;

const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <div className="bg-white/95 dark:bg-gray-800/95 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-6 text-left flex items-center justify-between focus:outline-none"
      >
        <h4 className="font-semibold text-gray-900 dark:text-white pr-4">{question}</h4>
        <ChevronDown
          className={`w-5 h-5 text-gray-500 transition-transform duration-200 flex-shrink-0 ${
            isOpen ? 'transform rotate-180' : ''
          }`}
        />
      </button>
      <div
        className={`overflow-hidden transition-all duration-200 ${
          isOpen ? 'max-h-96' : 'max-h-0'
        }`}
      >
        <p className="p-6 pt-0 text-gray-700 dark:text-gray-300">{answer}</p>
      </div>
    </div>
  );
};

// Add this new component for the interactive demo
const SpreadsheetDemo = () => {
  const [messages, setMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [currentStage, setCurrentStage] = useState(0);

  // Demo conversation flow
  const demoFlow = [
    {
      user: "Show me revenue trends for Q2",
      assistant: {
        content: "Based on the Q2 data analysis, here are the key findings:",
        analysis: [
          "Revenue peaked in April with a 47% increase compared to Q1",
          "Strong performance in new product categories",
          "Significant growth in enterprise segment"
        ],
        visualization: true
      }
    },
    {
      user: "Compare with last year's performance",
      assistant: {
        content: "Here's a year-over-year comparison:",
        analysis: [
          "Overall growth of 32% compared to previous year",
          "New markets contributed to 40% of the increase",
          "Customer retention improved by 25%"
        ],
        visualization: true
      }
    }
  ];

  useEffect(() => {
    const playDemo = async () => {
      if (currentStage < demoFlow.length) {
        // Show user message
        setIsTyping(true);
        await new Promise(resolve => setTimeout(resolve, 1000));
        setMessages(prev => [...prev, { role: 'user', content: demoFlow[currentStage].user }]);
        setIsTyping(false);

        // Show assistant response
        await new Promise(resolve => setTimeout(resolve, 1000));
        setMessages(prev => [...prev, { role: 'assistant', ...demoFlow[currentStage].assistant }]);

        // Move to next stage after delay
        await new Promise(resolve => setTimeout(resolve, 5000));
        setCurrentStage(prev => prev + 1);
      } else {
        // Reset demo after completion
        await new Promise(resolve => setTimeout(resolve, 3000));
        setMessages([]);
        setCurrentStage(0);
      }
    };

    playDemo();
  }, [currentStage]);

  return (
    <div className="relative z-10 bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6">
      <div className="space-y-4">
        {/* Messages */}
        {messages.map((message, index) => (
          <div
            key={index}
            className={cn(
              "flex mb-4 animate-slideIn",
              message.role === 'user' ? 'justify-end' : 'justify-start'
            )}
          >
            <div className={cn(
              "rounded-2xl shadow-lg backdrop-blur-sm max-w-[85%]",
              message.role === 'user'
                ? "bg-gradient-to-br from-blue-600 to-purple-600 text-white p-4"
                : "bg-white/90 dark:bg-gray-900/90 border border-gray-200/30 dark:border-gray-700/30 p-6"
            )}>
              <div className="space-y-4">
                {/* Message Content */}
                <div className="prose dark:prose-invert">
                  {message.content}
                </div>

                {/* Analysis Section */}
                {message.analysis && (
                  <div className="mt-4 space-y-2">
                    {message.analysis.map((point, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <ArrowRight className="w-4 h-4 mt-1 text-blue-500" />
                        <span className="text-gray-700 dark:text-gray-300">{point}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Visualization */}
                {message.visualization && (
                  <div className="mt-4 bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                    <div className="h-48 relative">
                      <div className="absolute bottom-0 left-0 w-full h-full flex items-end justify-around">
                        {[60, 85, 40, 90, 70].map((height, i) => (
                          <div 
                            key={i}
                            className="w-1/6 bg-gradient-to-t from-purple-500 to-blue-500 rounded-t-md opacity-80"
                            style={{ 
                              height: `${height}%`,
                              animation: `grow 0.8s ease-out ${i * 0.1}s`
                            }}
                          />
                        ))}
                      </div>
                    </div>
                    <div className="flex justify-between mt-2 text-sm text-gray-500">
                      <span>Jan</span>
                      <span>Feb</span>
                      <span>Mar</span>
                      <span>Apr</span>
                      <span>May</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}

        {/* Typing Indicator */}
        {isTyping && (
          <div className="flex items-center space-x-3 p-4 bg-white/50 dark:bg-gray-800/50 rounded-xl">
            <div className="relative h-8 w-8 flex items-center justify-center">
              <div className="absolute inset-0 bg-blue-500/10 rounded-full animate-ping" />
              <Bot className="w-5 h-5 text-blue-500 animate-bounce" />
            </div>
            <div className="space-y-2 flex-1">
              <div className="text-sm text-gray-600 dark:text-gray-300 italic">
                Analyzing data...
              </div>
              <div className="flex space-x-1">
                {[...Array(3)].map((_, i) => (
                  <div 
                    key={i}
                    className="w-1.5 h-1.5 bg-blue-500/30 rounded-full animate-bounce"
                    style={{ animationDelay: `${i * 0.1}s` }}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Input Area */}
      <div className="mt-4 border-t pt-4">
        <div className="flex items-center space-x-2 relative">
          <input
            type="text"
            value={demoFlow[currentStage]?.user || ''}
            readOnly
            placeholder="Ask anything about your data..."
            className="flex-1 rounded-xl px-4 py-3 
              bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm
              border border-gray-200/50 dark:border-gray-700/50
              text-gray-900 dark:text-white
              shadow-sm"
          />
          <button
            className="px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-xl 
              hover:from-blue-700 hover:to-blue-600 shadow-lg hover:shadow-xl
              transition-all duration-300 transform hover:scale-[1.02]"
          >
            <Send className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

const LandingPage = () => {
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  const [currentLang, setCurrentLang] = useState('en');
  const t = translations[currentLang];
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
  const [isAnnual, setIsAnnual] = useState(false);

  const login = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        const response = await axios.post(
          `${API_URL}/auth/google/login`,
          { token: tokenResponse.access_token },
          {
            headers: {
              'Content-Type': 'application/json',
            }
          }
        );

        if (response.data?.access_token) {
          const userData = {
            access_token: response.data.access_token,
            token_type: response.data.token_type,
            ...response.data.user
          };
          // Store the user data
          localStorage.setItem('access_token', userData.access_token);
          localStorage.setItem('user', JSON.stringify(userData));
          window.location.href = '/chat'; // Redirect to chat page
        }
      } catch (error) {
        console.error('Login error:', error);
        // Handle error (maybe show a toast notification)
      }
    },
    onError: (error) => {
      console.error('Google login failed:', error);
      // Handle error (maybe show a toast notification)
    }
  });

  // Intersection Observer for animations
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in');
        }
      });
    }, { threshold: 0.1 });

    document.querySelectorAll('.animate-on-scroll').forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  // Calculate prices and savings
  const prices = {
    standard: {
      monthly: 14.99,
      annual: 149.99,
      savings: Math.round((14.99 * 12 - 149.99) / (14.99 * 12) * 100)
    },
    pro: {
      monthly: 29.99,
      annual: 299.99,
      savings: Math.round((29.99 * 12 - 299.99) / (29.99 * 12) * 100)
    }
  };

  const handleSubscribe = (plan) => {
    // Get user from localStorage if available
    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : null;

    const baseUrl = plan === 'standard' 
      ? 'https://buy.stripe.com/4gw6qr5DQ27UcmYcMN'
      : 'https://buy.stripe.com/3cs4ij2rE27Udr27ss';

    // Add client_reference_id and prefill customer email if user is logged in
    const url = new URL(baseUrl);
    if (user && user.id) {
      url.searchParams.append('client_reference_id', user.id);
      if (user.email) {
        url.searchParams.append('prefilled_email', user.email);
      }
    }

    window.location.href = url.toString();
  };

  const features = [
    {
      icon: <Bot className="w-8 h-8" />,
      title: "Chat with Documents",
      description: "Have natural conversations with your documents. Ask questions and get instant answers from any part of your files."
    },
    {
      icon: <FileSearch className="w-8 h-8" />,
      title: "Smart Search",
      description: "Find exactly what you're looking for, even if you don't remember the exact words. Our AI understands what you mean."
    },
    {
      icon: <Brain className="w-8 h-8" />,
      title: "Quick Understanding",
      description: "Get the main points of any document instantly. Perfect for research papers, reports, or long articles."
    },
    {
      icon: <FileStack className="w-8 h-8" />,
      title: "Work with Multiple Files",
      description: "Upload multiple documents and find connections between them. Great for research, study, or project work."
    },
    {
      icon: <Sparkles className="w-8 h-8" />,
      title: "Auto-Summaries",
      description: "Get quick summaries of any document. Save hours of reading time while catching all important details."
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Private & Secure",
      description: "Your documents stay private and secure. We use strong encryption to protect your sensitive information."
    }
  ];

  // Add smooth scroll handler
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Add structured data for search engines
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Predica.AI",
    "applicationCategory": "BusinessApplication",
    "description": "AI-powered document analysis platform for natural conversations with documents and spreadsheets",
    "offers": {
      "@type": "Offer",
      "price": "14.99",
      "priceCurrency": "USD"
    },
    "operatingSystem": "Web-based",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "ratingCount": "150"
    }
  };

  return (
    <div className={`min-h-screen ${isDark ? 'dark' : ''}`}>
      <style>{backgroundStyles}</style>
      <style>{animatedBackgroundStyles}</style>
      <div className="relative bg-white dark:bg-[#0D1117] transition-colors duration-300">
        {/* Animated Background Elements */}
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          <div className="animated-bg absolute inset-0" />
          <div className="mesh-grid absolute inset-0" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/50 to-white dark:via-[#0D1117]/50 dark:to-[#0D1117]" />
        </div>

        {/* Content wrapper */}
        <div className="relative">
          <Helmet>
            {/* Primary Meta Tags */}
            <title>Predica.AI - AI-Powered Document Intelligence Platform</title>
            <meta name="title" content="Predica.AI - AI-Powered Document Intelligence Platform" />
            <meta name="description" content="Transform your document workflow with AI-powered analysis. Chat naturally with your documents, get instant insights, and analyze data across multiple files." />
            <meta name="keywords" content="document analysis, AI document processing, semantic search, document intelligence, PDF analysis, document chat, spreadsheet analysis, enterprise document management" />
            
            {/* Open Graph / Facebook */}
            <meta property="og:type" content="website" />
            <meta property="og:url" content="https://predica.ai/" />
            <meta property="og:title" content="Predica.AI - AI-Powered Document Intelligence Platform" />
            <meta property="og:description" content="Transform your document workflow with AI-powered analysis. Chat naturally with your documents and get instant insights." />
            <meta property="og:image" content="https://predica.ai/og-image.jpg" />

            {/* Twitter */}
            <meta property="twitter:card" content="summary_large_image" />
            <meta property="twitter:url" content="https://predica.ai/" />
            <meta property="twitter:title" content="Predica.AI - AI-Powered Document Intelligence Platform" />
            <meta property="twitter:description" content="Transform your document workflow with AI-powered analysis. Chat naturally with your documents and get instant insights." />
            <meta property="twitter:image" content="https://predica.ai/twitter-image.jpg" />

            {/* Additional SEO Tags */}
            <meta name="robots" content="index, follow" />
            <meta name="googlebot" content="index, follow" />
            <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=5" />
            <meta name="author" content="Predica.AI" />
            <link rel="canonical" href="https://predica.ai/" />

            {/* Structured Data */}
            <script type="application/ld+json">
              {JSON.stringify(structuredData)}
            </script>

            {/* Preconnect to important third-party domains */}
            <link rel="preconnect" href="https://fonts.googleapis.com" />
            <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="true" />
            
            {/* Theme Color */}
            <meta name="theme-color" content={isDark ? '#0D1117' : '#ffffff'} />
          </Helmet>

          {/* Updated Navigation - More minimal */}
          <nav className="fixed w-full z-50 bg-white/80 dark:bg-[#0D1117]/80 backdrop-blur-sm border-b border-gray-100 dark:border-gray-800/50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex justify-between h-16 items-center">
                {/* Logo */}
                <div className="flex items-center">
                  <a href="/" className="flex items-center">
                    <span className="text-lg sm:text-xl font-bold bg-gradient-to-r from-purple-600 to-indigo-600 dark:from-purple-400 dark:to-indigo-400 text-transparent bg-clip-text">
                      Predica.AI
                    </span>
                  </a>
                </div>

                {/* Desktop Navigation */}
                <div className="hidden md:flex items-center space-x-6">
                  {/* <button 
                    onClick={() => scrollToSection('features')} 
                    className="text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900"
                  >
                    Features
                  </button>
                  <button 
                    onClick={() => scrollToSection('pricing')} 
                    className="text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900"
                  >
                    Pricing
                  </button>*/}
                  <button
                    onClick={toggleTheme}
                    className="p-2 rounded-full text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
                  >
                    {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                  </button>
                  <button
                    onClick={() => login()}
                    className="bg-black dark:bg-white text-white dark:text-black px-6 py-2 rounded-full text-sm font-medium hover:bg-gray-900 dark:hover:bg-gray-100 transition-colors"
                  >
                    {t.nav.getStarted}
                  </button>
                </div>

                {/* Mobile Navigation */}
                <div className="flex md:hidden items-center space-x-4">
                  <button
                    onClick={toggleTheme}
                    className="p-2 rounded-full text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
                  >
                    {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                  </button>
                  <button
                    onClick={() => login()}
                    className="bg-black dark:bg-white text-white dark:text-black px-4 py-2 rounded-full text-sm font-medium hover:bg-gray-900 dark:hover:bg-gray-100 transition-colors"
                  >
                    Get Started
                  </button>
                </div>
              </div>
            </div>
          </nav>

          {/* Hero Section */}
          <section className="pt-24 pb-12 overflow-hidden px-4">
            <div className="max-w-7xl mx-auto">
              <div className="relative">
                {/* Subtle accent line */}
                <div className="absolute -top-10 left-1/2 transform -translate-x-1/2 w-20 h-px bg-gradient-to-r from-transparent via-gray-400 dark:via-gray-500 to-transparent" />
                
                {/* Hero content */}
                <div className="text-center mb-12">
                  <h1 className="text-4xl sm:text-6xl md:text-7xl lg:text-8xl font-bold mb-6 tracking-tight">
                    <span className="block text-gray-900 dark:text-white">Your data.</span>
                    <span className="block text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-indigo-600 dark:from-purple-400 dark:to-indigo-400">
                      Speaks to you.
                    </span>
                  </h1>
                  <p className="text-lg sm:text-xl md:text-2xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto mb-8 px-4">
                    Chat with your spreadsheets and documents naturally. Get instant insights from thousands of files with AI-powered analysis.
                  </p>
                  <button 
                    onClick={() => login()}
                    className="w-full sm:w-auto bg-black dark:bg-white text-white dark:text-black px-8 py-4 rounded-full text-lg font-medium hover:bg-gray-900 dark:hover:bg-gray-100 transition-colors floating"
                  >
                    Start Free Trial →
                  </button>

                  {/* Add badges below the CTA button */}
                </div>
              </div>
            </div>
          </section>

          {/* Feature sections with subtle animations */}
          <section className="py-16 sm:py-20">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="space-y-16 sm:space-y-24">
                {/* Each feature section gets subtle hover and animation effects */}
                <div className="group relative py-8">
                  <div className="max-w-6xl mx-auto px-4">
                    {/* Video Container */}
                    <div className="relative">
                      <video 
                        className="w-full rounded-2xl"
                        autoPlay 
                        loop 
                        muted 
                        playsInline
                        preload="auto"
                        disablePictureInPicture
                        disableRemotePlayback
                        controlsList="nodownload nofullscreen noremoteplayback"
                        style={{
                          transform: 'translate3d(0, 0, 0)',
                          WebkitTransform: 'translate3d(0, 0, 0)',
                          backfaceVisibility: 'hidden',
                          WebkitBackfaceVisibility: 'hidden',
                          perspective: '1000',
                          WebkitPerspective: '1000',
                          willChange: 'transform',
                          width: '100%',
                          height: 'auto'
                        }}
                      >
                        <source 
                          src="/0204.webm" 
                          type="video/webm; codecs=vp8,vorbis"
                        />
                        Your browser does not support the video tag.
                      </video>
                      
                      {/* Reduced number of decorative elements and simplified blur */}
                      <div className="absolute -top-10 -right-10 w-96 h-96 bg-purple-500/5 rounded-full blur-xl" />
                    </div>
                  </div>
                </div>

                {/* Universal Document Support */}
                <div className="group relative">
                  <div className="grid md:grid-cols-2 gap-8 md:gap-16 items-center">
                    {/* Feature content */}
                    <div className="text-center md:text-left">
                      <div className="mb-6 inline-flex items-center px-3 py-1 rounded-full bg-blue-100 dark:bg-blue-900/30">
                        <FileStack className="w-4 h-4 text-blue-600 mr-2" />
                        <span className="text-sm text-blue-600">Universal Support</span>
                      </div>
                      <h2 className="text-4xl md:text-5xl font-bold mb-8 tracking-tight text-gray-900 dark:text-white">
                        All your documents. One AI assistant.
                      </h2>
                      <p className="text-xl text-gray-600 dark:text-gray-300">
                        From spreadsheets to presentations, PDFs to Word docs. Ask questions across all your files simultaneously.
                      </p>
                      <div className="mt-8 grid grid-cols-2 gap-6">
                        {[
                          {
                            icon: <FileText className="w-6 h-6 text-blue-600" />,
                            title: "PDF & Word",
                            desc: "Smart parsing of complex documents"
                          },
                          {
                            icon: <Table className="w-6 h-6 text-green-600" />,
                            title: "Excel & CSV",
                            desc: "Deep analysis of tabular data"
                          },
                          {
                            icon: <PresentationIcon className="w-6 h-6 text-orange-600" />,
                            title: "PowerPoint",
                            desc: "Extract insights from presentations"
                          },
                          {
                            icon: <Files className="w-6 h-6 text-purple-600" />,
                            title: "Multi-file Analysis",
                            desc: "Cross-reference across documents"
                          }
                        ].map((item) => (
                          <div key={item.title} className="p-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
                            <div className="mb-3">{item.icon}</div>
                            <h3 className="font-semibold text-gray-900 dark:text-white mb-1">{item.title}</h3>
                            <p className="text-sm text-gray-600 dark:text-gray-300">{item.desc}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                    {/* Feature visual */}
                    <div className="relative order-first md:order-last">
                      <div className="relative z-10 bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6">
                        {/* Document Processing Animation */}
                        <div className="space-y-6">
                          {/* File Upload Visual */}
                          <div className="relative h-64 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4">
                            <div className="absolute inset-0 flex flex-col items-center justify-center">
                              <div className="flex space-x-4">
                                {[
                                  { icon: FileText, color: 'text-blue-500', label: 'PDF' },
                                  { icon: Table, color: 'text-green-500', label: 'XLSX' },
                                  { icon: FileText, color: 'text-orange-500', label: 'DOCX' }
                                ].map((file, i) => (
                                  <div 
                                    key={i}
                                    className="flex flex-col items-center space-y-2 animate-float"
                                    style={{ animationDelay: `${i * 0.2}s` }}
                                  >
                                    <file.icon className={`w-12 h-12 ${file.color}`} />
                                    <span className="text-sm text-gray-500">{file.label}</span>
                                  </div>
                                ))}
                              </div>
                              <div className="mt-8 w-full max-w-xs bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                <div className="bg-blue-500 h-2 rounded-full animate-progress" />
                              </div>
                            </div>
                          </div>

                          {/* Processing Status */}
                          <div className="space-y-3">
                            <div className="flex items-center space-x-2 text-green-500">
                              <CheckCircle className="w-5 h-5" />
                              <span className="text-sm">OCR Processing Complete</span>
                            </div>
                            <div className="flex items-center space-x-2 text-blue-500">
                              <Bot className="w-5 h-5" />
                              <span className="text-sm">AI Analysis Ready</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Decorative Background */}
                      <div className="absolute -top-4 -left-4 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl" />
                    </div>
                  </div>
                </div>

                {/* Advanced Analytics */}
                <div className="group relative">
                  <div className="grid md:grid-cols-2 gap-8 md:gap-16 items-center">
                    {/* Feature content */}
                    <div className="text-center md:text-left">
                      <div className="mb-6 inline-flex items-center px-3 py-1 rounded-full bg-green-100 dark:bg-green-900/30">
                        <TrendingUp className="w-4 h-4 text-green-600 mr-2" />
                        <span className="text-sm text-green-600">Advanced Analytics</span>
                      </div>
                      <h2 className="text-4xl md:text-5xl font-bold mb-8 tracking-tight text-gray-900 dark:text-white">
                        From data to decisions.
                      </h2>
                      <p className="text-xl text-gray-600 dark:text-gray-300">
                        Get instant visualizations, trend analysis, and actionable insights from your data.
                      </p>
                      <div className="mt-8 space-y-6">
                        {[
                          {
                            title: "Natural Language Queries",
                            desc: "Ask complex questions in plain English: 'Show me sales trends for Q2 compared to last year'"
                          },
                          {
                            title: "Auto-generated Visualizations",
                            desc: "AI automatically chooses the best chart type to represent your data"
                          },
                          {
                            title: "Cross-sheet Analysis",
                            desc: "Compare and analyze data across multiple sheets and files simultaneously"
                          }
                        ].map((item) => (
                          <div key={item.title} className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm">
                            <h3 className="font-semibold text-gray-900 dark:text-white mb-2">{item.title}</h3>
                            <p className="text-gray-600 dark:text-gray-300">{item.desc}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                    {/* Feature visual */}
                    <div className="relative order-first md:order-last">
                      <div className="relative z-10 bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6">
                        {/* Interactive Analytics Dashboard */}
                        <div className="space-y-6">
                          {/* Chart Types */}
                          <div className="grid grid-cols-2 gap-4">
                            {/* Line Chart */}
                            <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
                              <div className="h-32 relative">
                                <div className="absolute inset-0 flex items-end">
                                  <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                                    <path
                                      d="M0,50 Q25,20 50,50 T100,50"
                                      fill="none"
                                      stroke="url(#gradient)"
                                      strokeWidth="2"
                                      className="text-purple-500"
                                    />
                                    <defs>
                                      <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                        <stop offset="0%" stopColor="#8B5CF6" />
                                        <stop offset="100%" stopColor="#6366F1" />
                                      </linearGradient>
                                    </defs>
                                  </svg>
                                </div>
                              </div>
                              <p className="text-sm text-gray-600 dark:text-gray-300 mt-2">Revenue Trends</p>
                            </div>

                            {/* Bar Chart */}
                            <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
                              <div className="h-32 flex items-end justify-around">
                                {[40, 70, 30, 85, 50].map((height, i) => (
                                  <div
                                    key={i}
                                    className="w-4 bg-gradient-to-t from-green-500 to-green-400 rounded-t"
                                    style={{ height: `${height}%` }}
                                  />
                                ))}
                              </div>
                              <p className="text-sm text-gray-600 dark:text-gray-300 mt-2">Monthly Growth</p>
                            </div>
                          </div>

                          {/* AI Insights */}
                          <div className="bg-blue-500/10 p-4 rounded-lg">
                            <div className="flex items-center space-x-2 mb-3">
                              <Brain className="w-5 h-5 text-blue-500" />
                              <span className="font-medium text-blue-600">AI Analysis</span>
                            </div>
                            <p className="text-sm text-gray-600 dark:text-gray-300">
                              Sales have increased by 47% in Q2. Key growth markets include:
                            </p>
                            <div className="mt-2 grid grid-cols-2 gap-2">
                              <div className="flex items-center space-x-2">
                                <LineChart className="w-4 h-4 text-green-500" />
                                <span className="text-sm text-gray-600 dark:text-gray-300">North America (+58%)</span>
                              </div>
                              <div className="flex items-center space-x-2">
                                <LineChart className="w-4 h-4 text-blue-500" />
                                <span className="text-sm text-gray-600 dark:text-gray-300">Europe (+42%)</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Decorative Elements */}
                      <div className="absolute -top-4 -right-4 w-72 h-72 bg-green-500/10 rounded-full blur-3xl" />
                      <div className="absolute -bottom-8 -left-8 w-48 h-48 bg-blue-500/10 rounded-full blur-2xl" />
                    </div>
                  </div>
                </div>

                {/* Updated FAQ section */}
                {/* <div className="mt-32">
                  <h3 className="text-3xl font-bold text-center mb-12 text-gray-900 dark:text-white">
                    Frequently Asked Questions
                  </h3>
                  <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                    {[
                      {
                        q: "How does the AI-powered document analysis work?",
                        a: "Our advanced AI technology reads and understands your documents, allowing you to ask questions naturally and get instant, accurate answers. It works with various document types including PDFs, Word documents, PowerPoint presentations, and spreadsheets."
                      },
                      {
                        q: "What types of documents can I analyze with Predica.AI?",
                        a: "Predica.AI supports a wide range of document formats including PDF, DOCX, PPTX, XLSX, CSV, and TXT files. Our system can handle text-heavy documents, presentations, spreadsheets, and even scanned documents through OCR technology."
                      },
                      {
                        q: "How accurate is the document analysis and OCR?",
                        a: "Our standard OCR accuracy exceeds 95% for clear text, while our Premium OCR (available in Pro plan) achieves up to 99% accuracy and includes support for complex layouts, tables, and multiple languages."
                      },
                      {
                        q: "Can I chat with multiple documents simultaneously?",
                        a: "Yes! You can upload multiple documents and ask questions across all of them. Our AI will analyze relationships between documents and provide comprehensive answers from all relevant sources."
                      },
                      {
                        q: "Is my data secure when using Predica.AI?",
                        a: "Absolutely. We use enterprise-grade encryption for all documents, and your data is never shared with third parties. Documents are processed in secure environments and can be removed at your request."
                      },
                      {
                        q: "What's the difference between Standard and Pro OCR?",
                        a: "Standard OCR handles basic text recognition, while Pro OCR includes advanced features like table extraction, handwriting recognition, multiple language support, and maintains formatting accuracy."
                      },
                      {
                        q: "How does the 7-day free trial work?",
                        a: "You can try all features of either our Standard or Pro plan for 7 days without any charge. No credit card is required to start your trial, and you can upgrade or cancel at any time."
                      },
                      {
                        q: "Can I export the insights and analysis from my documents?",
                        a: "Yes, both plans allow you to export insights, summaries, and analysis results. Pro users get additional export formats and the ability to use our API for custom integrations."
                      }
                    ].map((item) => (
                      <FAQItem key={item.q} question={item.q} answer={item.a} />
                    ))}
                  </div>
                </div> */}
              </div>
            </div>
          </section>

          {/* Insert Testimonials Section above Pricing Section */}
          <section className="py-16 sm:py-20 px-4 bg-gray-50 dark:bg-gray-800">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-center mb-12 text-gray-900 dark:text-white">
                What Our Customers Say
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                <div className="bg-white dark:bg-gray-700 rounded-2xl p-8 shadow-xl">
                  <p className="text-lg italic text-gray-700 dark:text-gray-300">
                    "Predica.AI transformed our workflow. The analysis was accurate and insightful."
                  </p>
                  <div className="mt-4">
                    <span className="font-bold text-gray-900 dark:text-white">Brian Solyhan</span>
                    <span className="text-sm text-gray-500 dark:text-gray-400">, Data Analyst, Acme Corp</span>
                  </div>
                </div>
                <div className="bg-white dark:bg-gray-700 rounded-2xl p-8 shadow-xl">
                  <p className="text-lg italic text-gray-700 dark:text-gray-300">
                    "The intuitive design and rapid results have made document processing a breeze."
                  </p>
                  <div className="mt-4">
                    <span className="font-bold text-gray-900 dark:text-white">Jane Smith</span>
                    <span className="text-sm text-gray-500 dark:text-gray-400">, Product Manager, Crossword Inc</span>
                  </div>
                </div>
                <div className="bg-white dark:bg-gray-700 rounded-2xl p-8 shadow-xl">
                  <p className="text-lg italic text-gray-700 dark:text-gray-300">
                    "I love how easily I get the insights I need from my documents. Highly recommended!"
                  </p>
                  <div className="mt-4">
                    <span className="font-bold text-gray-900 dark:text-white">Alex Johnson</span>
                    <span className="text-sm text-gray-500 dark:text-gray-400">, Data Analyst, Gamma LLC</span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Pricing Section */}
          <section className="py-16 sm:py-20 relative px-4">
            {/* Remove the dimming gradient and replace with a more subtle one */}
            <div className="absolute inset-0 bg-gradient-to-b from-gray-50/50 to-white dark:from-gray-900/30 dark:to-[#0D1117]/30" />
            
            {/* Add a more vibrant mesh background */}
            <div className="absolute inset-0">
              <div className="absolute inset-0 opacity-10 dark:opacity-20"
                style={{
                  backgroundImage: `radial-gradient(circle at 25% 25%, rgba(139, 92, 246, 0.2), transparent),
                                   radial-gradient(circle at 75% 75%, rgba(99, 102, 241, 0.2), transparent)`
                }}
              />
            </div>

            <div className="relative max-w-7xl mx-auto">
              {/* Update pricing header with stronger contrast */}
              <div className="text-center mb-12 sm:mb-20">
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6 sm:mb-8">
                  <span className="block text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-indigo-600 dark:from-purple-400 dark:to-indigo-400">
                    Simple pricing for everyone
                  </span>
                </h2>
                <p className="text-xl text-gray-800 dark:text-gray-200 max-w-2xl mx-auto">
                  Choose the perfect plan for your document analysis needs
                </p>
              </div>

              {/* Update pricing toggle with more contrast */}
              <div className="flex justify-center mb-12">
                <div className="bg-white/90 dark:bg-gray-800/90 rounded-full p-1 inline-flex items-center shadow-lg">
                  <button 
                    onClick={() => setIsAnnual(false)}
                    className={`px-6 py-2 rounded-full font-medium transition-colors ${
                      !isAnnual 
                        ? 'bg-purple-600 text-white' 
                        : 'text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    Monthly
                  </button>
                  <button 
                    onClick={() => setIsAnnual(true)}
                    className={`px-6 py-2 rounded-full font-medium transition-colors ${
                      isAnnual 
                        ? 'bg-purple-600 text-white' 
                        : 'text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    Annually
                  </button>
                </div>
              </div>

              {/* Update pricing cards with stronger shadows and better contrast */}
              <div className="grid sm:grid-cols-2 gap-8 max-w-4xl mx-auto">
                {/* Standard Plan */}
                <div className="relative bg-white/95 dark:bg-gray-800/95 rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
                  {/* Trial Badge */}
                  <div className="absolute -top-4 right-4">
                    <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-4 py-1 rounded-full text-sm font-medium shadow-lg">
                      3-day free trial
                    </div>
                  </div>

                  <div className="mb-8">
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Standard</h3>
                    <div className="flex items-baseline">
                      <span className="text-5xl font-bold text-gray-900 dark:text-white">
                        ${isAnnual ? prices.standard.annual : prices.standard.monthly}
                      </span>
                      <span className="text-gray-500 dark:text-gray-400 ml-2">
                        /{isAnnual ? 'year' : 'month'}
                      </span>
                      {isAnnual && (
                        <span className="ml-2 px-2 py-1 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 text-sm rounded-full">
                          Save {prices.standard.savings}%
                        </span>
                      )}
                    </div>
                    <p className="text-gray-600 dark:text-gray-400 mt-4">Perfect for individuals and small teams</p>
                  </div>

                  <ul className="space-y-4 mb-8">
                    {[
                      "50 documents per month",
                      "100MB per document max file size",
                      "Support for PPTX, Word, PDF, Spreadsheet",
                      "Advanced analytics",
                      "Multilingual documents/tables",
                      "Basic OCR",
                      "Standard support"
                    ].map((feature) => (
                      <li key={feature} className="flex items-center">
                        <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                        <span className="text-gray-600 dark:text-gray-300">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <button 
                    onClick={() => login()}
                    className="w-full py-3 px-6 rounded-xl bg-gray-900 dark:bg-white text-white dark:text-gray-900 font-medium hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors"
                  >
                    Start Free Trial
                  </button>
                </div>

                {/* Pro Plan */}
                <div className="relative bg-gradient-to-b from-purple-600 to-indigo-600 rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
                  {/* Trial Badge */}
                  <div className="absolute -top-4 right-4">
                    <div className="bg-white text-purple-600 px-4 py-1 rounded-full text-sm font-medium shadow-lg">
                      3-day free trial
                    </div>
                  </div>

                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-purple-600/20 to-indigo-600/20 blur-xl" />
                  <div className="mb-8">
                    <h3 className="text-2xl font-bold text-white mb-4">Pro</h3>
                    <div className="flex items-baseline">
                      <span className="text-5xl font-bold text-white">
                        ${isAnnual ? prices.pro.annual : prices.pro.monthly}
                      </span>
                      <span className="text-purple-200 ml-2">
                        /{isAnnual ? 'year' : 'month'}
                      </span>
                      {isAnnual && (
                        <span className="ml-2 px-2 py-1 bg-white/20 text-white text-sm rounded-full">
                          Save {prices.pro.savings}%
                        </span>
                      )}
                    </div>
                    <p className="text-purple-100 mt-4">For power users and teams</p>
                  </div>

                  <ul className="space-y-4 mb-8">
                    {[
                      "Unlimited documents per month",
                      "100MB per document max file size",
                      "Support for all document types",
                      "Premium OCR & data extraction",
                      "Advanced analytics",
                      "Priority support",
                      "Custom folders"
                    ].map((feature) => (
                      <li key={feature} className="flex items-center">
                        <CheckCircle className="w-5 h-5 text-purple-200 mr-3" />
                        <span className="text-white">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <button 
                    onClick={() => login()}
                    className="w-full py-3 px-6 rounded-xl bg-white text-purple-600 font-medium hover:bg-purple-50 transition-colors"
                  >
                    Start Free Trial
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="py-16 sm:py-20 relative px-4">
            <div className="max-w-7xl mx-auto">
              <h3 className="text-3xl font-bold text-center mb-12 text-gray-900 dark:text-white">
                Frequently Asked Questions
              </h3>
              <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                {[
                  {
                    q: "How does the AI-powered document analysis work?",
                    a: "Our advanced AI technology reads and understands your documents, allowing you to ask questions naturally and get instant, accurate answers. It works with various document types including PDFs, Word documents, PowerPoint presentations, and spreadsheets."
                  },
                  {
                    q: "What types of documents can I analyze with Predica.AI?",
                    a: "Predica.AI supports a wide range of document formats including PDF, DOCX, PPTX, XLSX, CSV, and TXT files. Our system can handle text-heavy documents, presentations, spreadsheets, and even scanned documents through OCR technology."
                  },
                  {
                    q: "How accurate is the document analysis and OCR?",
                    a: "Our standard OCR accuracy exceeds 95% for clear text, while our Premium OCR (available in Pro plan) achieves up to 99% accuracy and includes support for complex layouts, tables, and multiple languages."
                  },
                  {
                    q: "Can I chat with multiple documents simultaneously?",
                    a: "Yes! You can upload multiple documents and ask questions across all of them. Our AI will analyze relationships between documents and provide comprehensive answers from all relevant sources."
                  },
                  {
                    q: "Is my data secure when using Predica.AI?",
                    a: "Absolutely. We use enterprise-grade encryption for all documents, and your data is never shared with third parties. Documents are processed in secure environments and can be removed at your request."
                  },
                  {
                    q: "What's the difference between Standard and Pro OCR?",
                    a: "Standard OCR handles basic text recognition, while Pro OCR includes advanced features like table extraction, handwriting recognition, multiple language support, and maintains formatting accuracy."
                  },
                  {
                    q: "How does the 7-day free trial work?",
                    a: "You can try all features of either our Standard or Pro plan for 7 days without any charge. No credit card is required to start your trial, and you can upgrade or cancel at any time."
                  },
                  {
                    q: "Can I export the insights and analysis from my documents?",
                    a: "Yes, both plans allow you to export insights, summaries, and analysis results. Pro users get additional export formats and the ability to use our API for custom integrations."
                  }
                ].map((item) => (
                  <FAQItem key={item.q} question={item.q} answer={item.a} />
                ))}
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* Replace existing footer with Footer component */}
      <Footer />
    </div>
  );
};

export default LandingPage;



